import React, { FC, useEffect, useState } from "react";
import Modal from "../../../../common/Modal/Modal";
import ModalBody from "../../../../common/Modal/ModalBody/ModalBody";
import ModalContent from "../../../../common/Modal/ModalContent/ModalContent";
import ModalHeader from "../../../../common/Modal/ModalHeader/ModalHeader";
import { useToggleState } from "../../../../../utils/common/dataTypes/boolean";
import { CircularProgress } from "@mui/material";
import { classNames } from "../../../../../utils/common/styling";
import {
  CURRENCY_SYMBOLS,
  CURRENCY_STRINGS,
} from "../../../../../features/EUD/modules/Payments/constants/Payments.constants";
import { IPurchaseHistory } from "../../../../../features/EUD/modules/PurchaseHistory/IPurchaseHistory";
import {
  IPostBookingItem,
  IPostBookingsModalProps,
} from "./PostBookingsModal.interfaces";
import { styles } from "./PostBookingsModal.styles";

const formatPurchaseDate = (value?: string | null) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatAmount = (amount?: number | null) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return "N/A";
  }
  return Number(amount).toFixed(2);
};

const getCurrencyCode = (currency?: string | null) => {
  const code = (
    currency || "INR"
  ).toUpperCase() as IPurchaseHistory.ICurrencyCode;
  return CURRENCY_STRINGS[code] || code;
};

const getCurrencySymbol = (currency?: string | null) => {
  const code = (
    currency || "INR"
  ).toUpperCase() as IPurchaseHistory.ICurrencyCode;
  return CURRENCY_SYMBOLS[code] || code;
};

const PostBookingsModal: FC<IPostBookingsModalProps> = ({
  open,
  onClose,
  callGetPostBookings,
  notifyError,
}) => {
  const [bookings, setBookings] = useState<IPostBookingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, startLoading, stopLoading] = useToggleState(false);

  useEffect(() => {
    if (!open) {
      setBookings([]);
      setTotal(0);
      return;
    }

    startLoading();
    callGetPostBookings()
      .then((result) => {
        setBookings(result.bookings || []);
        setTotal(result.total || 0);
      })
      .catch(() => {
        notifyError("Error fetching bookings, please try again");
        onClose();
      })
      .finally(stopLoading);
  }, [open]);

  const showingFrom = bookings.length ? 1 : 0;
  const showingTo = bookings.length;

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalContent className={styles.modalContent}>
        <ModalHeader onClose={onClose}>
          <div className={styles.headerText}>Bookings ({total})</div>
        </ModalHeader>
        <ModalBody className={styles.body}>
          {isLoading ? (
            <div className={styles.loader}>
              <CircularProgress size={28} />
            </div>
          ) : bookings.length === 0 ? (
            <div className={styles.empty}>No bookings yet for this post</div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Customer Name</th>
                      <th className={styles.th}>Email</th>
                      <th className={styles.th}>Phone</th>
                      <th className={styles.th}>Price paid</th>
                      <th className={styles.th}>Purchase date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => {
                      const currencyCode = getCurrencyCode(booking.currency);
                      const isInr = currencyCode === "INR";
                      const phone = [
                        booking.customer_country_code,
                        booking.customer_phone,
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <tr key={booking.booking_id}>
                          <td className={styles.td}>
                            {booking.customer_name || "Customer"}
                          </td>
                          <td className={styles.td}>
                            {booking.customer_email || "N/A"}
                          </td>
                          <td className={styles.td}>{phone || "N/A"}</td>
                          <td className={styles.td}>
                            <div className={styles.priceCell}>
                              <span
                                className={classNames(
                                  styles.currencyBadge,
                                  isInr
                                    ? styles.currencyBadgeInr
                                    : styles.currencyBadgeOther,
                                )}
                              >
                                {currencyCode}
                              </span>
                              <span>
                                {getCurrencySymbol(booking.currency)}
                                {formatAmount(booking.amount)}
                              </span>
                            </div>
                          </td>
                          <td className={styles.td}>
                            {formatPurchaseDate(booking.purchased_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className={styles.footer}>
                Showing{" "}
                <span className={styles.footerStrong}>
                  {showingFrom}-{showingTo}
                </span>{" "}
                of <span className={styles.footerStrong}>{total}</span> items
              </div>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PostBookingsModal;
